import { supabase } from '../lib/supabase';
import { orgStore } from '../lib/orgStore';
import { stripImmutable } from '../lib/orgScope';

const INSIGHT_BUCKET = 'insight_attachments';
const INSIGHT_SELECT = '*, companies:company_id (id, name), customer_users:customer_user_id (id, name)';

export type Sentiment = 'positive' | 'neutral' | 'negative';

export interface InsightLink {
  type: string;
  label: string;
}

export interface Insight {
  id: string;
  org_id: string;
  project_id: string | null;
  title: string;
  details: string | null;
  customer_user_id: string | null;
  company_id: string | null;
  assignee_id: string | null;
  sentiment: Sentiment | null;
  tags: string[];
  themes: string[];
  links: InsightLink[];
  processed: boolean;
  created_by: string;
  created_at: string;
  updated_at: string;
  companies?: { id: string; name: string } | null;
  customer_users?: { id: string; name: string } | null;
}

export interface InsightAttachment {
  id: string;
  insight_id: string;
  org_id: string;
  file_name: string;
  file_type: string;
  storage_path: string;
  url: string;
  created_by: string;
  created_at: string;
}

export interface InsightComment {
  id: string;
  insight_id: string;
  org_id: string;
  text: string;
  author_id: string;
  author_name: string;
  created_at: string;
}

function requireOrgId(): string {
  const orgId = orgStore.getOrgId();
  if (!orgId) throw new Error('No organization found.');
  return orgId;
}

// Throws if there's no signed-in user. Used by every code path that
// creates a row whose `created_by` column is NOT NULL — silently
// inserting an empty string would later violate the FK on the user
// and surface as a cryptic 23502 / 23503 in the Supabase error log.
async function requireCurrentUserId(): Promise<string> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user?.id) {
    throw new Error('You must be signed in to perform this action.');
  }
  return user.id;
}

let _insightsCache: { key: string; data: Insight[]; expiry: number } | null = null;
const CACHE_TTL = 60_000;

function invalidateInsightsCache() { _insightsCache = null; }

export const insightService = {
  async getInsights(projectId?: string | null): Promise<Insight[]> {
    const key = projectId ?? '__all__';
    const now = Date.now();
    if (_insightsCache && _insightsCache.key === key && now < _insightsCache.expiry) {
      return _insightsCache.data;
    }
    let query = supabase.from('insights').select(INSIGHT_SELECT).order('created_at', { ascending: false });
    if (projectId) query = query.eq('project_id', projectId);
    const { data, error } = await query;
    if (error) throw error;
    const rows = (data ?? []) as unknown as Insight[];
    _insightsCache = { key, data: rows, expiry: now + CACHE_TTL };
    return rows;
  },

  async getInsight(id: string): Promise<Insight> {
    const { data, error } = await supabase.from('insights').select(INSIGHT_SELECT).eq('id', id).single();
    if (error) throw error;
    return data as unknown as Insight;
  },

  async createInsight(input: {
    title: string;
    details?: string | null;
    project_id?: string | null;
    customer_user_id?: string | null;
    company_id?: string | null;
    assignee_id?: string | null;
    sentiment?: Sentiment | null;
    tags?: string[];
    themes?: string[];
    links?: InsightLink[];
    processed?: boolean;
  }): Promise<Insight> {
    const created_by = await requireCurrentUserId();
    const { data, error } = await supabase
      .from('insights')
      .insert({ org_id: requireOrgId(), created_by, ...input })
      .select(INSIGHT_SELECT)
      .single();
    if (error) throw error;
    invalidateInsightsCache();
    return data as unknown as Insight;
  },

  async updateInsight(id: string, updates: Partial<Pick<Insight, 'title' | 'details' | 'project_id' | 'customer_user_id' | 'company_id' | 'assignee_id' | 'sentiment' | 'tags' | 'themes' | 'links' | 'processed'>>): Promise<Insight> {
    const { data, error } = await supabase
      .from('insights')
      .update({ ...stripImmutable(updates), updated_at: new Date().toISOString() })
      .eq('id', id)
      .eq('org_id', requireOrgId())
      .select(INSIGHT_SELECT)
      .single();
    if (error) throw error;
    invalidateInsightsCache();
    return data as unknown as Insight;
  },

  async deleteInsight(id: string): Promise<void> {
    const { error } = await supabase
      .from('insights')
      .delete()
      .eq('id', id)
      .eq('org_id', requireOrgId());
    if (error) throw error;
    invalidateInsightsCache();
  },

  // ── Comments ──────────────────────────────────────────────
  async getComments(insightId: string): Promise<InsightComment[]> {
    const { data, error } = await supabase
      .from('insight_comments')
      .select('*')
      .eq('insight_id', insightId)
      .order('created_at', { ascending: true });
    if (error) throw error;
    return (data ?? []) as InsightComment[];
  },

  async addComment(insightId: string, text: string, authorName?: string): Promise<InsightComment> {
    const author_id = await requireCurrentUserId();
    const { data, error } = await supabase
      .from('insight_comments')
      .insert({ insight_id: insightId, org_id: requireOrgId(), text, author_id, author_name: authorName ?? '' })
      .select()
      .single();
    if (error) throw error;
    return data as InsightComment;
  },

  // ── Attachments (Supabase Storage) ────────────────────────
  async getAttachments(insightId: string): Promise<InsightAttachment[]> {
    const { data, error } = await supabase
      .from('insight_attachments')
      .select('*')
      .eq('insight_id', insightId)
      .order('created_at', { ascending: true });
    if (error) throw error;
    return data ?? [];
  },

  async uploadAttachment(insightId: string, orgId: string, file: File): Promise<InsightAttachment> {
    const ext = file.name.split('.').pop() ?? 'bin';
    const storagePath = `${orgId}/${insightId}/${Date.now()}_${Math.random().toString(36).slice(2)}.${ext}`;

    const { error: uploadErr } = await supabase.storage
      .from(INSIGHT_BUCKET)
      .upload(storagePath, file, { contentType: file.type, upsert: false });
    if (uploadErr) throw new Error(`Storage upload failed: ${uploadErr.message}`);

    const { data: { publicUrl } } = supabase.storage.from(INSIGHT_BUCKET).getPublicUrl(storagePath);
    // Throws early if not signed in — created_by is NOT NULL + FK to auth.users.
    const userId = await requireCurrentUserId();

    const { data, error: insertErr } = await supabase
      .from('insight_attachments')
      .insert({
        insight_id: insightId,
        org_id: orgId,
        file_name: file.name,
        file_type: file.type,
        storage_path: storagePath,
        url: publicUrl,
        created_by: userId,
      })
      .select()
      .single();
    if (insertErr) { await supabase.storage.from(INSIGHT_BUCKET).remove([storagePath]); throw new Error(`DB insert failed: ${insertErr.message}`); }
    return data;
  },

  async deleteAttachment(attachment: InsightAttachment): Promise<void> {
    await supabase.storage.from(INSIGHT_BUCKET).remove([attachment.storage_path]);
    const { error } = await supabase
      .from('insight_attachments')
      .delete()
      .eq('id', attachment.id)
      .eq('org_id', requireOrgId());
    if (error) throw error;
  },
};
